// apps/web/src/pages/auth/ForgotPassword.tsx
//
// Plan 04-12b Task 1 — initiate password reset.
//
// FLOW:
//   1. Single email input + submit
//   2. POST /api/auth/forgot-password { email }
//   3. ALWAYS shows uniform "If an account exists for this email, you'll
//      receive a reset link" — server returns 200 unconditionally per Plan
//      04-10 T-04-10-03 enumeration defense.
//
// CONTEXT:
//   The server emits an audit row with errorCode=UNKNOWN_EMAIL on the
//   email-not-found path; the UI never differentiates. T8 defense.

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { api } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { EmailField } from "@/components/forms/EmailPasswordFields";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email"),
});

type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPassword(): React.ReactElement {
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  async function onSubmit(values: ForgotPasswordValues): Promise<void> {
    try {
      await api<{ ok: boolean }>("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: values.email }),
      });
    } catch {
      // Server is supposed to return 200 uniformly. Any failure here is a
      // transport-level error; we still surface the uniform success state
      // (T8 defense — we MUST NOT reveal whether the email existed).
    } finally {
      setSubmitted(true);
    }
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Forgot password</CardTitle>
          <CardDescription>
            Enter your email and we'll send you a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <Alert>
              <AlertTitle>Check your email</AlertTitle>
              <AlertDescription>
                If an account exists for that email, you'll receive a reset link
                within a few minutes. The link expires in 15 minutes.
              </AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                <EmailField
                  control={form.control}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  disabled={form.formState.isSubmitting}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/auth/sign-in" className="font-medium underline">
              Back to sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
